import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  Autocomplete,
  Tooltip,
  ListItemText,
} from "@mui/material";
import { Colors } from "../../../../Styles/Colors";
import ShowComponents from "../Common/ShowComponent";
import { renderIconButton, renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import {
  CommonDeleteButton,
  CommonEditButton,
  DeletePopup,
} from "../../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../../Utility/ImagePaths";
import RestAPIService from "../../../../Utility/Services/RestAPIService";

const headers = [
  "Category Code",
  "Category Name",
  "Disease Code",
  "Disease Name",
  "Sub division code",
  "Sub Division",
  "Action",
];

const DocAllopathicDiagnosis = ({ Title, ...props }) => {
  const [state, setState] = useState({
    // input row
    categoryCodeValue: null,    
    categoryNameValue: null,    
    diseaseCodeValue: null,     
    diseaseNameValue: null,     
    subDivCodeValue: null,      
    subDivNameValue: null,      

    // master lists & fetched data
    categoryList: [],
    diseaseList: [],
    subDivisionList: [],
    diagnosisData: [],
    subDivisionMaster: [],

    // UI
    isEdit: false,
    selectedId: null,
    postLoading: false,
    dltLineClk: false,
    deleteLineItem: null,
    removeLineData: false,
  });

  const updateState = (updates) => setState((prev) => ({ ...prev, ...updates }));

  // lazy-load when component visible
  const getApiCall = useCallback(() => {
    getCategoryData();
    getDiseaseData();
    getSubDivisionMaster();
    getDiagnosisData();
  }, [props.appointmentId, props.isFromIp, props.ipAdmissionId]);

  useEffect(() => {
    props.registerRefreshFunction("allopathic_diagnosis", getDiagnosisData);
  }, []);

  const getCategoryData = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_CATEGORY_MASTER);
      if (res?.data?.status === "success") {
        updateState({ categoryList: res?.data?.data?.filter(i => i.is_active) || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  // master: disease
  const getDiseaseData = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_DISEASE_MASTER);
      if (res?.data?.status === "success") {
        updateState({ diseaseList: res?.data?.data?.filter(i => i.is_active) || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  const getSubDivisionMaster = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_SUBDIVISION_MASTER);
      if (res?.data?.status === "success") {
        updateState({ subDivisionMaster: res.data.data?.filter(i => i.is_active) || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  // fetch subdivisions for a given disease id (call when disease selected)
  const getSubDivisionsByDisease = async (diseaseId) => {
    try {
      if (!diseaseId) {
        return [];
      }
      return state.subDivisionMaster.filter((item) => item.disease === diseaseId);
    } catch (e) {
      props.errorMessage(e.message);
      return [];
    }
  };

  // existing saved diagnosis rows
  const getDiagnosisData = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
      const url = `${Serviceurls.AYURVEDA_ALLOPATHIC_DIAGNOSIS}${params}`;
      const res = await RestAPIService.getAll(url);
      if (res?.data?.status === "success") {
        updateState({ diagnosisData: res?.data?.data || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  // clear input row
  const onClearHandler = () => {
    updateState({
      isEdit: false,
      selectedId: null,
      categoryCodeValue: null,
      categoryNameValue: null,
      diseaseCodeValue: null,
      diseaseNameValue: null,
      subDivCodeValue: null,
      subDivNameValue: null,
    });
  };

  // delete flow
  const deleteLinePop = () => {
    updateState({
      dltLineClk: false,
      deleteLineItem: null,
      removeLineData: false,
    });
  };

  const deleteLineItem = async () => {
    try {
      updateState({ removeLineData: true });
      // delete endpoint: /AYURVEDA_DIAGNOSIS_MASTER/{id}/
      await RestAPIService.delete(`${Serviceurls.AYURVEDA_ALLOPATHIC_DIAGNOSIS}${state.deleteLineItem?.id}/`);
      props.successMessage("Deleted successfully");
      deleteLinePop();
      getDiagnosisData();
    } catch (e) {
      updateState({ removeLineData: false });
      props.errorMessage(e?.response?.data?.message || e.message);
    }
  };

  // create/update
  const postDiagnosis = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const data = {
        id: state.selectedId ? state.selectedId : null,
        category: state.categoryNameValue?.id || (state.categoryCodeValue?.id || ""),
        disease: state.diseaseCodeValue?.id || (state.diseaseCodeValue?.id || ""),
        subdivision: state.subDivCodeValue?.id || (state.subDivNameValue?.id || ""),
      };
      if (isFromIp) {
        data.ip_admission_id = ipAdmissionId;
        data.clinic_id = clinicId;
        data.doctor_id = doctorId;
      } else {
        data.appointment_id = appointmentId;
      }

      const params = Serviceurls.AYURVEDA_ALLOPATHIC_DIAGNOSIS;
      updateState({ postLoading: true });

      const apiCall = state.selectedId
        ? RestAPIService.update(state.selectedId, data, params)
        : RestAPIService.create(data, params);

      const res = await apiCall;
      if (res?.data?.status === "success") {
        props.successMessage(res.data.message || "Saved successfully");
        onClearHandler();
        getDiagnosisData();
      } else {
        props.errorMessage(res?.data?.message || "Save failed");
      }
      updateState({ postLoading: false });
    } catch (e) {
      updateState({ postLoading: false });
      props.errorMessage(e?.response?.data?.message || e.message);
    }
  };

  // edit: populate top row
  const onEdit = (item) => {
    // For consistency, set both code/name objects if item contains code/name pairs or nested details
    const categoryObj = item.category_details
    const diseaseObj = item.disease_details 
    const subDivObj = item.subdivision_details

    updateState({
      isEdit: true,
      selectedId: item.id,
      categoryCodeValue: categoryObj,
      categoryNameValue: categoryObj,
      diseaseCodeValue: diseaseObj,
      diseaseNameValue: diseaseObj,
      subDivCodeValue: subDivObj,
      subDivNameValue: subDivObj,
    });
  };

  const disableBtn = useMemo(() => {
    return !(
      state.categoryCodeValue &&
      state.categoryNameValue &&
      state.diseaseCodeValue &&
      state.diseaseNameValue
    ) || props.isLocked;
  }, [
    state.categoryCodeValue,
    state.categoryNameValue,
    state.diseaseCodeValue,
    state.diseaseNameValue,
    props.isLocked,
  ]);

  // ================= Render helpers (same style as systemic) =================
  const renderAutoComplete = (label, stateKey, list, showKey, disabled = false) => {
    const selectedValue = state[stateKey] || null;
    return (
      <Autocomplete
        size="small"
        options={list || []}
        disableClearable
        popupIcon={null}
        getOptionLabel={(option) => (typeof option === "string" ? option : option[showKey] || "")}
        value={selectedValue}
        disabled={disabled}
        onChange={async (e, newVal) => {
          updateState({ [stateKey]: newVal });
          if (stateKey === "categoryCodeValue" || stateKey === "categoryNameValue") {
            const catObj = newVal || null;
            updateState({ categoryCodeValue: catObj, categoryNameValue: catObj });
            return;
          }
          
          if (stateKey === "diseaseCodeValue" || stateKey === "diseaseNameValue") {
            const diseaseObj = newVal || null;
            updateState({ diseaseCodeValue: diseaseObj, diseaseNameValue: diseaseObj, subDivCodeValue: null, subDivNameValue: null });
            if (diseaseObj?.id) {
              const diseaseId = diseaseObj.id
              const subs = await getSubDivisionsByDisease(diseaseId);
              updateState({subDivisionList: subs}) 
              if (subs && subs.length === 1) {
                const subObj = subs[0];
                updateState({ subDivCodeValue: subObj, subDivNameValue: subObj });
              } else {
                // keep subDiv fields null (user must choose)
                updateState({ subDivCodeValue: null, subDivNameValue: null });
              }
            } else {
              // no disease selected -> clear subs
              updateState({ subDivCodeValue: null, subDivNameValue: null });
            }
            return;
          }

          // if subdivision selected, set both code & name state keys
          if (stateKey === "subDivCodeValue" || stateKey === "subDivNameValue") {
            const subObj = newVal || null;
            updateState({ subDivCodeValue: subObj, subDivNameValue: subObj });
            return;
          }
        }}
        renderOption={(propsOpt, option) => (
          <li {...propsOpt} style={{ padding: "0vh 1.5vh" }}>
            <ListItemText
              primary={
                option[showKey]?.length > 30 ? (
                  <Tooltip title={option[showKey]} placement="top">
                    <div>{option[showKey]?.slice(0, 21) + "..."}</div>
                  </Tooltip>
                ) : (
                  option[showKey]
                )
              }
            />
          </li>
        )}
        renderInput={(params) => <TextField {...params} size="small" label={label} autoComplete="off" />}
        sx={{ width: "100%" }}
      />
    );
  };

 // ================= UI =================
  return (
    <ShowComponents onIntersection={getApiCall}>
      <Card
        sx={{
          width: "100%",
          height: "fit-content",
          padding: "0.3vw",
          marginBottom: "0.5vw",
          border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`,
        }}
      >
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
          {renderTitle("Allopathic Diagnosis", props.isSelected)}
        </Box>

        <Table size="small" stickyHeader aria-label="allopathic diagnosis" sx={{ maxHeight: "28vw", overflowY: "auto" }}>
          <TableHead>
            <TableRow>
              {headers.map((h, idx) => (
                <TableCell
                  key={idx}
                  align={idx === headers.length - 1 ? "center" : "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9vw",
                    color: Colors.SecondaryText,
                    backgroundColor: Colors.Background,
                    px: "0.2vw",
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* INPUT ROW */}
            <TableRow sx={{ "& td": { px: "0.2vw", verticalAlign: "middle" } }}>
              <TableCell sx={{ width: "10vw" }}>
                {renderAutoComplete("Category Code *", "categoryCodeValue", state.categoryList, "category_code")}
              </TableCell>

              <TableCell sx={{ width: "12vw" }}>
                {renderAutoComplete("Category Name *", "categoryNameValue", state.categoryList, "category_name")}
              </TableCell>

              <TableCell sx={{ width: "10vw" }}>
                {renderAutoComplete("Disease Code *", "diseaseCodeValue", state.diseaseList, "disease_code")}
              </TableCell>

              <TableCell sx={{ width: "12vw" }}>
                {renderAutoComplete("Disease Name *", "diseaseNameValue", state.diseaseList, "disease_name")}
              </TableCell>

              <TableCell sx={{ width: "10vw" }}>
                {/* Subdivision options depend on selected disease (we show empty list if none) */}
                <Autocomplete
                  size="small"
                  options={state.subDivisionList || []}
                  disableClearable
                  popupIcon={null}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.subdivision_code || "")}
                  value={state.subDivCodeValue || null}
                  disabled={!(state.diseaseCodeValue || state.diseaseNameValue)}
                  onChange={async (e, newVal) => {
                    // if newVal is null, clear both
                    if (!newVal) {
                      updateState({ subDivCodeValue: null, subDivNameValue: null });
                      return;
                    }
                    updateState({ subDivCodeValue: newVal, subDivNameValue: newVal });
                  }}
                  renderOption={(propsOpt, option) => (
                    <li {...propsOpt} style={{ padding: "0vh 1.5vh" }}>
                      <ListItemText primary={option.subdivision_code} />
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Sub division code" size="small" />}
                  sx={{ width: "100%" }}
                />
              </TableCell>

              <TableCell sx={{ width: "14vw" }}>
                <Autocomplete
                  size="small"
                  options={state.subDivisionList || []}
                  disableClearable
                  popupIcon={null}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.subdivision_name || "")}
                  value={state.subDivNameValue || null}
                  disabled={!(state.diseaseCodeValue || state.diseaseNameValue)}
                  onChange={(e, newVal) => {
                    if (!newVal) {
                      updateState({ subDivCodeValue: null, subDivNameValue: null });
                      return;
                    }
                    updateState({ subDivCodeValue: newVal, subDivNameValue: newVal });
                  }}
                  renderOption={(propsOpt, option) => (
                    <li {...propsOpt} style={{ padding: "0vh 1.5vh" }}>
                      <ListItemText primary={option.subdivision_name} />
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Sub Division" size="small" />}
                  sx={{ width: "100%" }}
                />
              </TableCell>

              <TableCell align="center" sx={{ width: "1vw" }}>
                {renderIconButton(
                  state.isEdit ? "Update" : "Add",
                  disableBtn ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                  async () => {
                    if ((state.diseaseCodeValue || state.diseaseNameValue) && (!state.subDivCodeValue && !state.subDivNameValue)) {
                      const diseaseId = (state.diseaseCodeValue && (state.diseaseCodeValue.id || state.diseaseCodeValue.code)) ||
                                        (state.diseaseNameValue && (state.diseaseNameValue.id || state.diseaseNameValue.code));
                      const subs = await getSubDivisionsByDisease(diseaseId);
                      updateState({ subDivisionList: subs });
                      if (subs.length === 1) {
                        updateState({ subDivCodeValue: subs[0], subDivNameValue: subs[0] });
                      }
                    }
                    postDiagnosis();
                  },
                  disableBtn,
                  "2vw"
                )}
              </TableCell>
            </TableRow>

            {/* EXISTING ROWS */}
            {state.diagnosisData?.length > 0 ? (
              state.diagnosisData.map((item, idx) => (
                <TableRow key={item.id || idx} sx={{ "& td": { pl: "0.4vw", pr: "0.7vw", fontSize: "0.9vw" } }}>
                  <TableCell>{item.category_details?.category_code || "-"}</TableCell>
                  <TableCell>{item.category_details?.category_name || "-"}</TableCell>
                  <TableCell>{item.disease_details?.disease_code || "-"}</TableCell>
                  <TableCell>{item.disease_details?.disease_name || "-"}</TableCell>
                  <TableCell>{item.subdivision_details?.subdivision_code || "-"}</TableCell>
                  <TableCell>{item.subdivision_details?.subdivision_name || "-"}</TableCell>
                  <TableCell align="center">
                    <Box component={"div"} display={"flex"}>
                      <CommonEditButton
                        disable={state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => {
                          // when edit clicked, also prefetch subdivisions for that disease so sub fields populate
                          const diseaseId = item.disease_id || item.disease_code || item.disease;
                          (async () => {
                            const subs = await getSubDivisionsByDisease(diseaseId);
                            updateState({ subDivisionList: subs });
                            onEdit(item);
                          })();
                        }}
                      />
                      <CommonDeleteButton
                        disable={state.isEdit || state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => updateState({ dltLineClk: true, deleteLineItem: item })}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No Records To Be Shown
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {state.dltLineClk && (
          <DeletePopup
            DeletTitle={`Are you sure you want to delete ?`}
            deleteAlertPopupClose={deleteLinePop}
            removeData={deleteLineItem}
            disable={state.removeLineData}
          />
        )}
      </Card>
    </ShowComponents>
  );
};

export default DocAllopathicDiagnosis;
